import { useEffect, useRef } from 'react';

/**
 * Garde une lecture accordée sur celle du maître.
 *
 * Trois régimes, et le choix du seuil est tout le sujet :
 *
 *   - **moins de 0,3 s** : on ne touche à rien. Corriger en dessous produirait
 *     un tremblement permanent, la mesure étant elle-même bruitée.
 *   - **jusqu'à 2 s** : on ajuste la vitesse de quelques pour cent. L'écart se
 *     résorbe en quelques secondes, et personne ne voit rien — un saut, si.
 *   - **au-delà** : saut sec. Rattraper deux secondes en accélérant prendrait
 *     une minute ; arriver en cours de film, une éternité.
 *
 * Le maître n'est jamais corrigé : c'est lui la référence.
 */

const IGNORER = 0.3;
const SAUT = 2;
const VITESSE = 0.05;
const CADENCE_MS = 1000;

export function useAccord({ videoRef, actif, positionAttendue, enLecture }) {
  const refusRef = useRef(false);
  const attenduRef = useRef(positionAttendue);
  attenduRef.current = positionAttendue;

  useEffect(() => {
    if (!actif) {
      // Rendre la main proprement : sans ça, une vitesse corrigée resterait
      // en place après un passage de relais.
      if (videoRef.current) videoRef.current.playbackRate = 1;
      return undefined;
    }

    const battement = setInterval(() => {
      const video = videoRef.current;
      const cible = attenduRef.current();

      if (!video || cible === null || Number.isNaN(video.duration)) return;

      // Suivre l'état du maître avant de parler de dérive : être en pause
      // quand il joue n'est pas un écart de 40 s, c'est un autre problème.
      if (enLecture && video.paused) {
        video.play().catch(() => { refusRef.current = true; });
      } else if (!enLecture && !video.paused) {
        video.pause();
      }

      const ecart = cible - video.currentTime;
      const amplitude = Math.abs(ecart);

      if (amplitude >= SAUT) {
        video.currentTime = cible;
        video.playbackRate = 1;
      } else if (amplitude >= IGNORER) {
        video.playbackRate = ecart > 0 ? 1 + VITESSE : 1 - VITESSE;
      } else {
        video.playbackRate = 1;
      }
    }, CADENCE_MS);

    return () => {
      clearInterval(battement);
      if (videoRef.current) videoRef.current.playbackRate = 1;
    };
  }, [actif, enLecture, videoRef]);

  return { lectureRefusee: refusRef };
}
